import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * ApiResponseInterceptor
 *
 * The NestJS backend wraps every successful response with TransformInterceptor:
 *
 *   { success: boolean, data: T, timestamp: string }
 *
 * This interceptor transparently unwraps the `data` field so that all
 * services and components receive the actual payload directly, without
 * needing to navigate the envelope manually.
 *
 * Only applies to HTTP 2xx responses that carry the expected envelope shape.
 * Pass-through for any response that does not match (e.g. health checks,
 * third-party APIs, or error responses handled by the global error filter).
 */
@Injectable()
export class ApiResponseInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      map((event) => {
        if (event instanceof HttpResponse && this.isBackendEnvelope(event.body)) {
          // Unwrap: replace the full envelope with just the `data` payload
          return event.clone({ body: event.body.data });
        }
        return event;
      }),
    );
  }

  /**
   * Type guard — returns true only when the response body matches the
   * { success: boolean, data: unknown, timestamp: string } shape produced
   * by the backend TransformInterceptor.
   */
  private isBackendEnvelope(
    body: unknown,
  ): body is { success: boolean; data: unknown; timestamp: string } {
    return (
      body !== null &&
      typeof body === 'object' &&
      'success' in body &&
      'data' in body &&
      'timestamp' in body
    );
  }
}
